import { getFutureMatches } from '@datasert/cronjs-matcher';
import Info from '@mdi/svg/svg/information.svg?react';
import AlarmOff from '@mdi/svg/svg/shield-off.svg?react';
import { type CallhomeCamera, type SiteId } from '@solinkcorp/api-types';
import {
  Flex,
  Grid,
  LoadingSpinner,
  MDIIcon,
  Modal,
  Radio,
  RadioGroup,
  Select,
  type SelectOption,
  TextArea,
  TextField,
  toast,
  useTimeout,
} from '@solinkcorp/solink-components';
import AlarmOn from '@solinkcorp/solink-components/svg/alarm-on.svg?react';
import { ALL_LOCATIONS } from '@solinkcorp/vd-common';
import classNames from 'classnames';
import {
  type ScheduledActivityEntity,
  type ScheduledActivityMetadata,
  type ScheduledActivityRuleType,
  ScheduledActivityType,
} from 'clients/CallhomeApiTypes';
import { useAlarmCameras } from 'components/AlarmsPage/hooks/useAlarmCameras';
import { CommonContext } from 'components/Contexts';
import HorizontalCameraEditor from 'components/HorizontalCameraEditor/HorizontalCameraEditor';
import LocationSelector from 'components/LocationSelector/LocationSelector';
import isEmpty from 'lodash/isEmpty';
import AnalyticsLogger from 'models/AnalyticsLogger';
import moment from 'moment';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import useFluxStore from 'src/hooks/useFluxStore';
import useLocale from 'src/hooks/useLocale';
import { useTextAreaMaxLength } from 'src/hooks/useTextAreaMaxLength';
import CameraStore from 'stores/CameraStore';
import { LocationStore } from 'stores/LocationStore';
import { useSelector } from 'stores/store';
import type { ValueOf } from 'type-fest';
import { callhomeCameraToEventCamera } from 'utils/CameraUtil';
import CommonUtil from 'utils/CommonUtil';
import isShallowEqual from 'utils/isShallowEqual';
import LocaleUtil from 'utils/LocaleUtil';
import {
  CRON_ALL_VALUES,
  getActivityTypeOptions,
  getAIChatPreviewAnalytics,
  getCurrentMonthImageUsage,
  getDurationSecs,
  getNextStartTime,
  getRecurrence,
  getRepeatOptions,
  getRepeatOptionValue,
  getScheduledAIChatAnalytics,
  getScheduledExceptionsBaseAnalytics,
  getScheduledExceptionsPreviousEventAnalytics,
  ifHasUnexecutedEvents,
  parseCron,
  scheduledActivityRemapper,
} from 'utils/RecurrenceUtil';
import TimeFormatter from 'utils/TimeFormatter';
import { type CameraGroupWithCameras, type CamerasDictionaryCamera } from 'utils/types';
import {
  DEFAULT_TIMEZONE,
  DOES_NOT_REPEAT,
  MAX_HOURLY_OCCURRENCES_PER_MONTH,
  OCCUR_NEVER,
  OCCUR_ONCE,
  OPEN_AI_SUBTYPE,
  RECENT_CAMERAS_GROUP_NAME,
  REOCCUR_INDEFINITE,
  REOCCUR_NEVER_END,
  REPEAT_DAILY,
  REPEAT_EVERY_4H,
  REPEAT_EVERY_6H,
  REPEAT_HOURLY,
  REPEAT_MONTHLY,
  REPEAT_RULE_TYPE,
  ROUTE_ALARMS,
  SCHEDULED_INSPECTION_REASON,
  STATUS_FAILED,
  STATUS_SUCCEEDED,
  TYPE_OF_ALARM_EXCEPTION,
} from 'vd-constants';
import ActivityExpiryControl from './ActivityExpiryControl';
import ActivityFormFooter from './ActivityFormFooter';
import ActivityTitle from './ActivityTitle';
import CamerasTimezoneSelect from './CamerasTimezoneSelect';
import DaysOfWeekPicker from './DaysOfWeekPicker';
import './ScheduledActivityForm.sass';
import ScheduledActivityTimePicker from './ScheduledActivityTimePicker';
import SpotCheckEventPreview from './SpotCheckEventPreview';
import useActiveSpotChecksImageUsage from './useActiveSpotChecksImageUsage';
import usePreviewSpotCheck from './usePreviewSpotCheck';
import {
  type EditRecurringActivityType,
  type NewActivityType,
  type SaveRecurringActivityType,
} from './useScheduledEventForm';
import { type UsageQuota } from './useSpotCheckUsage';

export type ScheduledEventFormSource = 'eventsPage' | 'alarmsPage';
interface ScheduledEventFormProps {
  activity?: ScheduledActivityEntity;
  isLoading?: boolean;
  enforceLocationId?: SiteId;
  enforceReason?: string;
  filterReasons?: string[];
  isOpen: boolean;
  title: string;
  maxDate?: Date | number;
  viewOnly?: boolean;
  saveRecurringActivity: SaveRecurringActivityType;
  editRecurringActivity: EditRecurringActivityType;
  closeScheduledEventForm: () => void;
  updateActivity?: (newEvent: ScheduledActivityEntity) => void;
  addActivity?: (newEvent: ScheduledActivityEntity) => void;
  source?: ScheduledEventFormSource;
  showCloseAction?: boolean;
  /** feature where creation/modification was done, added to analytics*/
  analyticsSource?: string;
  /** can modify original camera selected when activity was created */
  canEditOriginalCameras?: boolean;
  spotCheckUsageStats?: UsageQuota;
  allowableRepeatOptions?: string[];
}

type formPropName = 'title' | 'description' | 'reason' | 'alarm-enable';

const FIVE_MINUTES_MILLISEC = 5 * 60 * 1000;
const INITIAL_AI_CHAT_REOCCURRING_COUNT = 12;

export const AI_INSPECTION_GROUP_OPTIONS = {
  location: 'LOCATION',
  cameraGroup: 'CAMERA_GROUP',
} as const;

export const AI_INSPECTION_END_TYPE = {
  date: 'DATE',
  occurrence: 'OCCURRENCE',
  never: 'NEVER',
} as const;

const AI_CHAT_WEEKLY_SCHEDULE_OFF = 'ai-chat-schedule-off';
const AI_CHAT_WEEKLY_SCHEDULE_IN_SCHEDULE = 'ai-chat-schedule-in-schedule';
const AI_CHAT_WEEKLY_SCHEDULE_OUT_SCHEDULE = 'ai-chat-schedule-out-schedule';
type WeeklyScheduleOptions =
  'ai-chat-schedule-off' | 'ai-chat-schedule-in-schedule' | 'ai-chat-schedule-out-schedule';
const AI_CHAT_WEEKLY_SCHEDULE_OPTIONS = {
  [AI_CHAT_WEEKLY_SCHEDULE_IN_SCHEDULE]: {
    text: 'duringStoreOperatingHours',
    metadataFields: {
      weeklyScheduleId: 'aiInferredOffHours',
      inSchedulePeriods: false,
    },
  },
  [AI_CHAT_WEEKLY_SCHEDULE_OUT_SCHEDULE]: {
    text: 'duringStoreOffHours',
    metadataFields: {
      weeklyScheduleId: 'aiInferredOffHours',
      inSchedulePeriods: true,
    },
  },
  [AI_CHAT_WEEKLY_SCHEDULE_OFF]: {
    text: 'noWeeklySchedule',
    metadataFields: {
      weeklyScheduleId: null,
      inSchedulePeriods: true,
    },
  },
} as const;

export type AIInspectionDeviceType = ValueOf<typeof AI_INSPECTION_GROUP_OPTIONS>;
export type AiInspectionEndType = ValueOf<typeof AI_INSPECTION_END_TYPE>;

const displayDateTime = (time: Date | number, timezoneId: string) => {
  return moment(time)
    .tz(timezoneId)
    .format(`ll ${TimeFormatter.getString({ zeroPrefix: false, seconds: false })}`);
};

const ScheduledActivityForm: React.FC<ScheduledEventFormProps> = (props) => {
  const {
    activity,
    enforceLocationId,
    enforceReason,
    filterReasons,
    isLoading,
    isOpen,
    title: formTitle,
    saveRecurringActivity,
    editRecurringActivity,
    closeScheduledEventForm,
    updateActivity,
    addActivity,
    maxDate,
    viewOnly = false,
    source,
    showCloseAction = true,
    analyticsSource,
    canEditOriginalCameras,
    spotCheckUsageStats,
    allowableRepeatOptions,
  } = props;

  const cameraSelectorRef = useRef<HTMLDivElement>(null);

  const canEditCameras = !viewOnly;
  const locale = useLocale();
  const locations = useFluxStore(LocationStore, (store) => store.getPhysicalLocations());
  const locationsById = useFluxStore(LocationStore, (store) => store.getLocationsById());
  const globalSelectedLocationIds = useSelector((state) => state.filter.locationIds);
  const groupsWithCameras: CameraGroupWithCameras[] = useFluxStore(
    CameraStore,
    (store) =>
      store
        .getCameraGroups()
        .filter(
          (group: CameraGroupWithCameras) =>
            group.name !== RECENT_CAMERAS_GROUP_NAME && group.cameras.length > 0,
        ),
    isShallowEqual,
  );
  const { camerasDictionary } = useContext(CommonContext);
  const { createSpotCheckPreview } = usePreviewSpotCheck();
  const [spotCheckPreviewEventId, setSpotCheckPreviewEventId] = useState<string | undefined>(
    undefined,
  );

  const [activityType, setActivityType] = useState(() => {
    if (!activity && !enforceReason && !filterReasons) {
      return ScheduledActivityType.ALARM_EXCEPTION;
    } else if (
      // TODO: replace this with a check on the activity type when callhome can actually handle them
      (activity && activity.reason === ScheduledActivityType.SCHEDULED_INSPECTION) ||
      (enforceReason && enforceReason === SCHEDULED_INSPECTION_REASON) ||
      (filterReasons && filterReasons.includes(SCHEDULED_INSPECTION_REASON))
    ) {
      return ScheduledActivityType.SCHEDULED_INSPECTION;
    } else if (
      // TODO: replace this with a check on the activity type when callhome can actually handle them
      (activity && activity.reason === ScheduledActivityType.SCHEDULED_AI_CHAT) ||
      (enforceReason && enforceReason === OPEN_AI_SUBTYPE) ||
      (filterReasons && filterReasons.includes(OPEN_AI_SUBTYPE))
    ) {
      return ScheduledActivityType.SCHEDULED_AI_CHAT;
    }

    return ScheduledActivityType.ALARM_EXCEPTION;
  });

  const [everyAlarmCamera, setEveryAlarmCamera] = useState(
    // If editing an activity with empty cameraIds, it means 'use all alarm cameras', so this should be true
    () => !!(activity && isEmpty(activity.cameraIds)),
  );

  const isScheduledInspection = useMemo(
    () => activityType === ScheduledActivityType.SCHEDULED_INSPECTION,
    [activityType],
  );
  const isScheduledAIChat = useMemo(
    () => activityType === ScheduledActivityType.SCHEDULED_AI_CHAT,
    [activityType],
  );

  const isAlarmScheduleException = useMemo(
    () => activityType === ScheduledActivityType.ALARM_EXCEPTION,
    [activityType],
  );

  const activeSpotCheckImageUsage = useActiveSpotChecksImageUsage(isScheduledAIChat, activity?.id);

  const locationSelectorLocations = useMemo(() => {
    if (
      globalSelectedLocationIds.length === 0 ||
      (globalSelectedLocationIds.length === 1 && globalSelectedLocationIds[0] === ALL_LOCATIONS)
    ) {
      return locations;
    }
    return locations.filter((location) =>
      globalSelectedLocationIds.some((id: string) => id === location.id),
    );
  }, [globalSelectedLocationIds, locations]);

  const initialLocationId = useMemo(
    () => activity?.siteId || enforceLocationId || locationSelectorLocations?.[0]?.id,
    [activity?.siteId, enforceLocationId, locationSelectorLocations],
  );

  const [selectedLocationId, setSelectedLocationId] = useState(initialLocationId);
  const [eventInfoStyles, setEventInfoStyles] = useState<any>(() => ({
    transform: `translate(0, 0px)`,
    top: 0,
  }));

  const selectedLocation = useMemo(() => {
    return locationsById?.[selectedLocationId] || locationSelectorLocations[0];
  }, [locationSelectorLocations, locationsById, selectedLocationId]);

  const { timezone = DEFAULT_TIMEZONE, cameras: selectedLocationCameras } = selectedLocation || {
    timezone: undefined,
    cameras: undefined,
  };

  /** cameras that are selected by default and uneditable within HorizontalCameraEditor */
  const initialSelectedCameras = useMemo(() => {
    const initialSelectedCameras: { [key: string]: any } = {};
    if (activity && !isEmpty(activity.cameraIds)) {
      activity.cameraIds.forEach((cameraId) => {
        const camera = camerasDictionary[cameraId];
        if (!camera) {
          // camera selected during configuration no longer exists
          return;
        }
        const eventCamera = callhomeCameraToEventCamera(camera);
        initialSelectedCameras[eventCamera.id] = eventCamera;
      });
    }
    return initialSelectedCameras;
  }, [activity, camerasDictionary]);

  const initialStartTime = useMemo(() => {
    if (isScheduledAIChat && activity) {
      return activity.startsAt;
    }

    return activity
      ? getNextStartTime(activity, timezone)
      : moment().add(2, 'hour').startOf('hour').valueOf();
  }, [activity, timezone, isScheduledAIChat]);

  const initialEndTime = useMemo((): number => {
    if (isScheduledAIChat) {
      return activity
        ? activity.endsAt
        : moment.tz(Date.now(), timezone).endOf('day').unix() * 1000;
    }

    return activity
      ? getNextStartTime(activity, timezone) + activity.durationSecs * 1000
      : moment(initialStartTime).add(1, 'hour').valueOf();
  }, [activity, timezone, initialStartTime, isScheduledAIChat]);

  const initialHasUnexcutedEvents = useMemo(
    () => activity && ifHasUnexecutedEvents(activity, timezone),
    [activity, timezone],
  );

  const [selectedCameras, setSelectedCameras] = useState(() => {
    if (activityType === ScheduledActivityType.SCHEDULED_AI_CHAT && !activity) {
      return getEventCamerasObject(selectedLocationCameras ?? []);
    }
    return initialSelectedCameras;
  });
  const [startTime, setStartTime] = useState(initialStartTime);
  const [endTime, setEndTime] = useState(initialEndTime);
  const [title, setTitle] = useState(activity?.title || '');
  const [isTitleFocused, setIsTitleFocused] = useState(false);
  const [description, setDescription] = useState(() => {
    if (isScheduledAIChat) {
      return activity?.metadata?.aiChatPrompt || '';
    }
    return activity?.description || '';
  });
  const [alarmEnable, setAlarmEnable] = useState<boolean | undefined>(
    activity ? activity.isAlarmsEnabled : undefined,
  );
  const [isCameraSelectorOpen, setIsCameraSelectorOpen] = useState(false);
  const updateCameraSelectorOpen = (isSelectorOpen: boolean) =>
    setIsCameraSelectorOpen(isSelectorOpen);

  /** state for switching between location or camera group to determine available cameras */
  const [selectedDeviceType, setSelectedDeviceType] = useState<AIInspectionDeviceType>(() => {
    if (activity && activity.metadata?.aiChatCameraGroupId) {
      return AI_INSPECTION_GROUP_OPTIONS.cameraGroup;
    }
    return AI_INSPECTION_GROUP_OPTIONS.location;
  });

  const [selectedCameraGroupId, setSelectedCameraGroupId] = useState<string>(() => {
    /** activity was configured with camera group as cameras source */
    if (activity && activity.metadata?.aiChatCameraGroupId) {
      return activity.metadata?.aiChatCameraGroupId;
    }
    return groupsWithCameras[0]?.id ?? '';
  });
  const [selectedEndType, setSelectedEndType] = useState<AiInspectionEndType>(() => {
    if (activity && activity.endsAt !== REOCCUR_NEVER_END) {
      return 'DATE';
    }
    return 'OCCURRENCE';
  });
  const [aiChatAnswerFormat, setAiChatAnswerFormat] = useState<string>(() => {
    return activity?.metadata?.aiChatAnswerFormat || '';
  });
  const [selectedDaysOfWeek, setSelectedDaysOfWeek] = useState<number[]>(() => {
    if (activity) {
      const cron = parseCron(activity.rule);

      const dayOfWeek = cron.day;

      return dayOfWeek === CRON_ALL_VALUES ? [] : dayOfWeek.split(',').map((num) => parseInt(num));
    }
    return [];
  });
  const enforceReasonFromActivityType = useMemo(() => {
    if (enforceReason) {
      return enforceReason;
    }
    if (activityType === ScheduledActivityType.SCHEDULED_INSPECTION) {
      return SCHEDULED_INSPECTION_REASON;
    }
    if (activityType === ScheduledActivityType.SCHEDULED_AI_CHAT) {
      return SCHEDULED_INSPECTION_REASON;
    }
  }, [enforceReason, activityType]);

  const [reason, setReason] = useState(
    activity?.reason || enforceReason || enforceReasonFromActivityType || '',
  );

  const [recurrenceOptions, setRecurrenceOptions] = useState<SelectOption[]>([]);

  let initialRecurrence: string = DOES_NOT_REPEAT;
  if (activity) {
    initialRecurrence = getRepeatOptionValue(activity.rule, activity.reoccurringCount);
  }

  const [recurrence, setRecurrence] = useState(initialRecurrence);
  const [weeklyScheduleOption, setWeeklyScheduleOption] = useState<WeeklyScheduleOptions>(
    AI_CHAT_WEEKLY_SCHEDULE_IN_SCHEDULE,
  );
  const [reoccurringCount, setReoccurringCount] = useState(() => {
    if (activity) {
      return activity.reoccurringCount;
    } else if (isScheduledAIChat) {
      return INITIAL_AI_CHAT_REOCCURRING_COUNT;
    }
    return 1;
  });
  const [cronString, setCronString] = useState('');

  const [isTouched, setIsTouched] = useState<{ [key: string]: boolean }>({});
  const [newStartWithinOneHourFromNow, setStartWithinOneHourFromNow] =
    useState(initialHasUnexcutedEvents);
  const [newNextStartTime, setNextStartTime] = useState(initialStartTime);
  const [isSaving, setIsSaving] = useState(false);

  const { alarmCameras } = useAlarmCameras(selectedLocation, source === 'alarmsPage');

  const availableCameras = useMemo(() => {
    if (!selectedLocationCameras) {
      return [];
    }

    let camerasToUse: Array<CallhomeCamera | CamerasDictionaryCamera> = [];

    if (source === 'alarmsPage' && alarmCameras) {
      camerasToUse = alarmCameras;
    } else if (selectedDeviceType === AI_INSPECTION_GROUP_OPTIONS.cameraGroup) {
      camerasToUse =
        groupsWithCameras.find((group) => group.id === selectedCameraGroupId)?.cameras || [];
    } else {
      camerasToUse = selectedLocationCameras;
    }

    return camerasToUse.map((camera) => callhomeCameraToEventCamera(camera));
  }, [
    selectedLocationCameras,
    source,
    alarmCameras,
    groupsWithCameras,
    selectedDeviceType,
    selectedCameraGroupId,
  ]);

  const newActivity: NewActivityType = useMemo(() => {
    const isAlarmsEnabled =
      typeof alarmEnable === 'undefined'
        ? activityType !== ScheduledActivityType.SCHEDULED_AI_CHAT
        : alarmEnable;

    let descriptionToUse: string | undefined = description;
    let endAtToUse = REOCCUR_NEVER_END;
    let reoccurrenceCountToUse = reoccurringCount;
    const scheduledActivityMetadata: ScheduledActivityMetadata = {
      lastWriteTime: Date.now(),
    };

    if (isScheduledAIChat) {
      // ai chats don't have a description
      descriptionToUse = undefined;
      scheduledActivityMetadata.aiChatAnswerFormat = aiChatAnswerFormat;
      scheduledActivityMetadata.aiChatPrompt = description;

      if (selectedDeviceType === 'CAMERA_GROUP') {
        scheduledActivityMetadata.aiChatCameraGroupId = selectedCameraGroupId;
        /** save group name for parser thumbnail labelling */
        scheduledActivityMetadata.cameraGroupName =
          groupsWithCameras.find((group) => group.id === selectedCameraGroupId)?.name || '';
      } else {
        scheduledActivityMetadata.aiChatCameraGroupId = undefined;
      }

      if (recurrence === DOES_NOT_REPEAT) {
        reoccurrenceCountToUse = OCCUR_ONCE;
      } else if (selectedEndType === 'DATE') {
        endAtToUse = endTime;
        /** endTime is used by parser to end scheduling activity */
        reoccurrenceCountToUse = REOCCUR_NEVER_END;
      }
    }
    if (
      selectedDeviceType === 'LOCATION' &&
      weeklyScheduleOption &&
      isScheduledAIChat &&
      (recurrence === REPEAT_HOURLY ||
        recurrence === REPEAT_EVERY_6H ||
        recurrence === REPEAT_EVERY_4H) &&
      AI_CHAT_WEEKLY_SCHEDULE_OPTIONS[weeklyScheduleOption]
    ) {
      const scheduleMetadataFields =
        AI_CHAT_WEEKLY_SCHEDULE_OPTIONS[weeklyScheduleOption].metadataFields;
      scheduledActivityMetadata.weeklyScheduleId = scheduleMetadataFields.weeklyScheduleId;
      scheduledActivityMetadata.inSchedulePeriods = scheduleMetadataFields.inSchedulePeriods;
    }

    return {
      activityType,
      siteId: selectedLocationId,
      title,
      description: descriptionToUse,
      reason,
      // added by scheduledActivityRemapper
      cameraIds: [],
      durationSecs: getDurationSecs(startTime, endTime),
      // This should always be set as the activity's starting time.
      // Otherwise we can't tell if the activity is passed or not based on the CRON rule only.
      startsAt: startTime,
      endsAt: endAtToUse,
      ruleType: REPEAT_RULE_TYPE as ScheduledActivityRuleType,
      rule: cronString,
      reoccurringCount: reoccurrenceCountToUse,
      isAlarmsEnabled,
      metadata: scheduledActivityMetadata,
    };
  }, [
    activityType,
    alarmEnable,
    cronString,
    description,
    endTime,
    reason,
    reoccurringCount,
    selectedLocationId,
    startTime,
    title,
    isScheduledAIChat,
    aiChatAnswerFormat,
    recurrence,
    weeklyScheduleOption,
    selectedEndType,
    selectedDeviceType,
    selectedCameraGroupId,
    groupsWithCameras,
  ]);

  const onBlur = useCallback(
    (propName: formPropName) => {
      if (!isTouched[propName]) {
        const newIsTouched = {
          ...isTouched,
          [propName]: true,
        };
        setIsTouched(newIsTouched);
      }
    },
    [isTouched],
  );

  const onChange = useCallback(
    (propName: string, value: string | number) => {
      switch (propName) {
        case 'weeklyScheduleOption': {
          setWeeklyScheduleOption(value as WeeklyScheduleOptions);
          break;
        }
        case 'recurrence': {
          const newRecurrence = value as string;
          setRecurrence(newRecurrence);

          if (
            newRecurrence === REPEAT_EVERY_6H ||
            newRecurrence === REPEAT_DAILY ||
            newRecurrence === REPEAT_EVERY_4H
          ) {
            setSelectedDaysOfWeek([]);
          }
          break;
        }
        case 'title': {
          setTitle(value as string);
          break;
        }
        case 'description': {
          setDescription(value as string);
          break;
        }
        case 'reason': {
          setReason(value as string);
          break;
        }
        case 'alarm-enable': {
          setAlarmEnable(value === 'true');
          onBlur('alarm-enable');
          break;
        }
        case 'activity-type': {
          setActivityType(value as ScheduledActivityType);
          break;
        }
        case 'aiChatAnswerFormat': {
          setAiChatAnswerFormat(value as string);
        }
      }
    },
    [onBlur, setSelectedDaysOfWeek],
  );

  const scheduledExceptionsBaseAnalytics = useMemo(() => {
    return getScheduledExceptionsBaseAnalytics({
      startTime,
      endTime,
      recurrence,
      recurrenceOptions,
      title,
      description,
      locationId: selectedLocation?.id,
      locationName: selectedLocation?.name,
      typeOfException: alarmEnable ? TYPE_OF_ALARM_EXCEPTION.ARM : TYPE_OF_ALARM_EXCEPTION.DISARM,
    });
  }, [
    startTime,
    endTime,
    recurrence,
    title,
    description,
    selectedLocation,
    alarmEnable,
    recurrenceOptions,
  ]);

  const scheduledAIChatAnalytics = useMemo(() => {
    /** newActivity.rule is empty before cronString is updated via effect */
    if (!cronString) {
      return;
    }

    return getScheduledAIChatAnalytics(newActivity, selectedDeviceType, analyticsSource);
  }, [newActivity, selectedDeviceType, cronString, analyticsSource]);

  const saveNewScheduledActivity = useCallback(async () => {
    if (isSaving) {
      return; // Prevent multiple submissions
    }

    setIsSaving(true);
    let status = STATUS_SUCCEEDED;
    try {
      await saveRecurringActivity({
        newActivity: scheduledActivityRemapper(
          isAlarmScheduleException && everyAlarmCamera ? [] : selectedCameras,
          newActivity,
          canEditCameras,
        ),
        addActivity,
      });
    } catch (_error) {
      status = STATUS_FAILED;
    } finally {
      setIsSaving(false);
    }

    if (isScheduledAIChat) {
      AnalyticsLogger.scheduledSpotCheckCreated({ ...scheduledAIChatAnalytics, status });
    } else {
      AnalyticsLogger.alarmScheduledExceptionAdd({ ...scheduledExceptionsBaseAnalytics, status });
    }
  }, [
    newActivity,
    selectedCameras,
    saveRecurringActivity,
    addActivity,
    canEditCameras,
    scheduledExceptionsBaseAnalytics,
    scheduledAIChatAnalytics,
    isScheduledAIChat,
    isSaving,
    everyAlarmCamera,
    isAlarmScheduleException,
  ]);

  const logEditScheduledActivity = useCallback(
    (status: string) => {
      if (!activity) {
        throw new Error('activity is required to log edit scheduled activity');
      }

      if (isScheduledAIChat) {
        const previousScheduledAIChatAnalytics = getScheduledAIChatAnalytics(
          activity,
          activity.metadata?.aiChatCameraGroupId
            ? AI_INSPECTION_GROUP_OPTIONS.cameraGroup
            : AI_INSPECTION_GROUP_OPTIONS.location,
        );
        AnalyticsLogger.scheduledSpotCheckUpdated({
          ...scheduledAIChatAnalytics,
          previousSpotCheckStartTime: previousScheduledAIChatAnalytics.spotCheckStartTime,
          previousSpotCheckEndTime: previousScheduledAIChatAnalytics.spotCheckEndTime,
          previousSpotCheckRepeat: previousScheduledAIChatAnalytics.spotCheckRepeat,
          previousOpenAIChatPrompt: previousScheduledAIChatAnalytics.openAIChatPrompt,
          previousDeviceId: previousScheduledAIChatAnalytics.deviceId,
          previousPromptSource: previousScheduledAIChatAnalytics.promptSource,
          status,
        });
      } else {
        AnalyticsLogger.alarmScheduledExceptionUpdate({
          ...scheduledExceptionsBaseAnalytics,
          ...getScheduledExceptionsPreviousEventAnalytics({
            startTime: initialStartTime,
            endTime: initialEndTime,
            recurrence: initialRecurrence,
            recurrenceOptions,
            title: activity?.title,
            description: activity?.description,
            typeOfException: activity.isAlarmsEnabled
              ? TYPE_OF_ALARM_EXCEPTION.ARM
              : TYPE_OF_ALARM_EXCEPTION.DISARM,
          }),
          status,
          siteId: scheduledExceptionsBaseAnalytics?.locationId,
        });
      }
    },
    [
      activity,
      initialEndTime,
      initialRecurrence,
      initialStartTime,
      recurrenceOptions,
      scheduledAIChatAnalytics,
      scheduledExceptionsBaseAnalytics,
      isScheduledAIChat,
    ],
  );

  const editScheduledActivity = useCallback(async () => {
    if (!activity) {
      throw new Error('activity is required to log edit scheduled activity');
    }

    if (isSaving) {
      return; // Prevent multiple submissions
    }

    setIsSaving(true);
    let status = STATUS_SUCCEEDED;
    try {
      await editRecurringActivity(
        {
          activity,
          newActivity: scheduledActivityRemapper(
            isAlarmScheduleException && everyAlarmCamera ? [] : selectedCameras,
            newActivity,
            // allow using the currently selected set of cameras
            // if the exception is created from the alarms page the set will be empty (default to alarm cameras)
            // if the exception is created from direct API call or the events page,
            // the user has selected a different set that we should keep
            true,
          ),
          editActivity: updateActivity,
        },
        timezone,
      );
    } catch (_error) {
      status = STATUS_FAILED;
    } finally {
      setIsSaving(false);
    }

    logEditScheduledActivity(status);
  }, [
    activity,
    selectedCameras,
    timezone,
    editRecurringActivity,
    newActivity,
    updateActivity,
    logEditScheduledActivity,
    isSaving,
    everyAlarmCamera,
    isAlarmScheduleException,
  ]);

  /**
   * sets activity.reoccurringCount to 0 to prevent further execution of activity
   * this allows activity to be referenced if user wants to re-activate activity
   * */
  const onStopScheduledActivity = useCallback(() => {
    if (!activity) {
      throw new Error('activity is required to stop scheduled activity');
      return;
    }

    let updateStatus = STATUS_SUCCEEDED;

    try {
      editRecurringActivity(
        {
          activity,
          newActivity: { reoccurringCount: OCCUR_NEVER },
          editActivity: updateActivity,
        },
        timezone,
      );
    } catch (_error) {
      updateStatus = STATUS_FAILED;
    }

    logEditScheduledActivity(updateStatus);
  }, [activity, editRecurringActivity, logEditScheduledActivity, timezone, updateActivity]);

  const onCreateSpotCheckPreview = useCallback(async () => {
    const aiPreviewAnalytics = getAIChatPreviewAnalytics(newActivity, selectedDeviceType);
    try {
      const eventId = await createSpotCheckPreview(newActivity, selectedCameras);
      setSpotCheckPreviewEventId(eventId);
      AnalyticsLogger.spotCheckPreviewCreated({ ...aiPreviewAnalytics, status: STATUS_SUCCEEDED });
    } catch (_error) {
      toast.error(locale.general.unexpectedError);
      AnalyticsLogger.spotCheckPreviewCreated({ ...aiPreviewAnalytics, status: STATUS_FAILED });
    }
  }, [newActivity, selectedCameras, createSpotCheckPreview, selectedDeviceType, locale]);

  const updateStartTime = useCallback((startTime: Date | undefined) => {
    if (startTime) setStartTime(startTime.valueOf());
  }, []);
  const updateEndTime = useCallback((endTime: Date | undefined) => {
    if (endTime) setEndTime(endTime.valueOf());
  }, []);

  const timeError = useMemo(() => {
    if (endTime !== REOCCUR_NEVER_END && moment(startTime).isAfter(endTime)) {
      return locale.cloudShare.endTimeBeforeStartTime;
    }
    const diff = moment(startTime).valueOf() - Date.now();
    if (
      !moment(startTime).isSame(initialStartTime, 'minute') &&
      diff < FIVE_MINUTES_MILLISEC &&
      !viewOnly
    ) {
      return locale.alarms.atLeastFiveMinutesFromNow;
    }
    return '';
  }, [endTime, initialStartTime, locale, startTime, viewOnly]);

  const leftoverImageCredits = useMemo(() => {
    /** editing recurrence is disabled, all other changes don't affect image usage */
    const activityImageUsage = getCurrentMonthImageUsage(
      {
        startsAt: startTime,
        endsAt: selectedEndType === 'DATE' ? endTime : REOCCUR_INDEFINITE,
        reoccurringCount:
          reoccurringCount > MAX_HOURLY_OCCURRENCES_PER_MONTH
            ? MAX_HOURLY_OCCURRENCES_PER_MONTH
            : reoccurringCount,
        rule: cronString,
      },
      Object.keys(selectedCameras).length,
      timezone,
    );

    return (
      // fallback to 0 if spotCheckUsageStats are undefined, this prevents users from creating activity
      (spotCheckUsageStats?.usage.images?.remaining ?? 0) -
      (activeSpotCheckImageUsage + activityImageUsage)
    );
  }, [
    spotCheckUsageStats,
    startTime,
    endTime,
    reoccurringCount,
    selectedEndType,
    cronString,
    selectedCameras,
    timezone,
    activeSpotCheckImageUsage,
  ]);

  const hasImageCreditsError = useMemo(() => {
    /** allow users to edit prompt/response format for existing activities */
    if (!isScheduledAIChat || !!activity || viewOnly || !leftoverImageCredits) {
      return false;
    }

    return leftoverImageCredits < 0;
  }, [leftoverImageCredits, isScheduledAIChat, activity, viewOnly]);

  const {
    maxLength: titleMaxLength,
    isOverLimit: isTitleOverLimit,
    charactersAvailableString: titleCharactersAvailableString,
  } = useTextAreaMaxLength(title, 32);

  const canSave = useMemo(() => {
    return (
      startTime &&
      endTime &&
      !timeError &&
      title &&
      !isTitleOverLimit &&
      selectedLocationId &&
      !isEmpty(selectedCameras) &&
      // scheduled inspection reason is set at create time
      // (note: hack to put together a demo - can be put back as a free form text field
      // when callhome can ingest the activity type)
      (reason || isScheduledInspection || isScheduledAIChat) &&
      description &&
      // scheduled inspection `alarmEnable` field is currently hidden and value is not used
      (typeof alarmEnable !== 'undefined' || !isAlarmScheduleException) &&
      !hasImageCreditsError
    );
  }, [
    alarmEnable,
    description,
    endTime,
    isScheduledInspection,
    isScheduledAIChat,
    isAlarmScheduleException,
    reason,
    selectedCameras,
    selectedLocationId,
    startTime,
    timeError,
    title,
    hasImageCreditsError,
    isTitleOverLimit,
  ]);

  const canPreviewSpotCheck = useMemo((): boolean => {
    return !!title && !!description && Object.keys(selectedCameras).length > 0;
  }, [title, description, selectedCameras]);

  const hasScheduledActivityExpired = useMemo(() => {
    if (!activity) {
      return false;
    }

    const { rule, reoccurringCount, startsAt } = activity;

    if (reoccurringCount === REOCCUR_INDEFINITE) {
      return false;
    } else if (reoccurringCount === OCCUR_NEVER) {
      return true;
    }

    const nextStartTime = getFutureMatches(rule, {
      startAt: new Date(startsAt).toISOString(),
      matchCount: reoccurringCount,
      timezone,
    });

    return Date.now() > +new Date(nextStartTime[nextStartTime.length - 1]);
  }, [activity, timezone]);

  const modalActions = [
    ...(isScheduledAIChat && !hasScheduledActivityExpired && activity
      ? [
          {
            text: locale.general.delete,
            isPrimary: false,
            onClick: onStopScheduledActivity,
            tooltip: locale.scheduledAIChat.stopScheduledActivityTooltip,
          },
        ]
      : []),
    ...(!viewOnly && isScheduledAIChat && spotCheckPreviewEventId
      ? [
          {
            /** we allow previews to be created even when hasImageCreditsError === true */
            disabled: !canPreviewSpotCheck,
            text: locale.general.preview,
            isPrimary: false,
            onClick: onCreateSpotCheckPreview,
          },
        ]
      : []),
    ...(showCloseAction
      ? [
          {
            text: locale.general.close,
            isPrimary: false,
            onClick: closeScheduledEventForm,
          },
        ]
      : []),
    ...(viewOnly && !isScheduledAIChat
      ? []
      : [
          {
            disabled: !canSave || isSaving,
            text: locale.general.save,
            isPrimary: true,
            onClick: activity ? editScheduledActivity : saveNewScheduledActivity,
          },
        ]),
  ];

  const getCameraGroupCamerasEventObject = useCallback(
    (groupId: string) => {
      const cameras = groupsWithCameras.find((group) => group.id === groupId)?.cameras || [];

      return getEventCamerasObject(cameras);
    },
    [groupsWithCameras],
  );

  const onCameraSourceChange = useCallback(
    ({ value }: { value: AIInspectionDeviceType }) => {
      setSelectedDeviceType(value);

      if (value === 'CAMERA_GROUP') {
        setSelectedCameras(getCameraGroupCamerasEventObject(groupsWithCameras[0]?.id) ?? []);
      } else if (value === 'LOCATION') {
        setSelectedCameras(getEventCamerasObject(selectedLocationCameras ?? []));
      }
    },
    [
      setSelectedDeviceType,
      setSelectedCameras,
      groupsWithCameras,
      getCameraGroupCamerasEventObject,
      selectedLocationCameras,
    ],
  );

  const onCameraGroupChange = useCallback(
    ({ value: groupId }: { value: string }) => {
      setSelectedCameraGroupId(groupId);
      setSelectedCameras(getCameraGroupCamerasEventObject(groupId));
    },
    [setSelectedCameraGroupId, getCameraGroupCamerasEventObject, setSelectedCameras],
  );

  const handleLocationSelected = useCallback(
    (locationid: SiteId) => {
      setSelectedLocationId(locationid);

      if (!isScheduledAIChat) {
        return;
      }

      const location = locationsById?.[locationid];

      if (location) {
        setSelectedCameras(getEventCamerasObject(location.cameras));
      } else {
        setSelectedCameras({});
      }
    },
    [locationsById, isScheduledAIChat],
  );

  useEffect(() => {
    if (!activity?.deviceId && enforceLocationId) {
      setSelectedLocationId(enforceLocationId);
    }
  }, [activity?.deviceId, enforceLocationId]);

  // scheduled inspection should have endTime === startTime
  useEffect(() => {
    if (isScheduledInspection || (isScheduledAIChat && endTime < startTime)) {
      setEndTime(startTime);
    }
  }, [startTime, endTime, isScheduledInspection, isScheduledAIChat]);

  useEffect(() => {
    const repeatOptions = getRepeatOptions(startTime, locale, timezone, isScheduledAIChat);
    const filteredRepeatOptions = allowableRepeatOptions
      ? repeatOptions.filter((option) => allowableRepeatOptions.includes(option.value))
      : repeatOptions;

    setRecurrenceOptions(filteredRepeatOptions);
  }, [locale, startTime, timezone, isScheduledAIChat, allowableRepeatOptions]);

  useEffect(() => {
    const { cron, reoccurringCount: newCount } = getRecurrence(
      startTime,
      recurrence,
      timezone,
      selectedDaysOfWeek,
    );
    // we allow editing reoccurring count when the activity is an AI chat
    if (activityType !== ScheduledActivityType.SCHEDULED_AI_CHAT) {
      setReoccurringCount(newCount);
    }

    setCronString(cron);
  }, [recurrence, startTime, timezone, activityType, selectedDaysOfWeek]);

  useEffect(() => {
    if (activity && cronString) {
      const params = {
        rule: cronString,
        reoccurringCount,
        durationSecs: getDurationSecs(startTime, endTime),
        startsAt: startTime,
      };
      setStartWithinOneHourFromNow(ifHasUnexecutedEvents(params, timezone));
      setNextStartTime(getNextStartTime(params, timezone));
    } else {
      setStartWithinOneHourFromNow(false);
      setNextStartTime(startTime);
    }
  }, [activity, cronString, reoccurringCount, startTime, endTime, timezone]);

  // empty camera selection when selecting a different location
  useEffect(() => {
    if (!isScheduledAIChat) {
      setSelectedCameras(initialSelectedCameras);
    }
  }, [selectedLocationId, initialSelectedCameras, selectedCameraGroupId, isScheduledAIChat]);

  // When editing an activity with empty cameraIds (meaning "all alarm cameras"),
  // populate selectedCameras with all availableCameras once they're loaded
  useEffect(() => {
    if (
      activity &&
      isEmpty(activity.cameraIds) &&
      availableCameras.length > 0 &&
      isEmpty(selectedCameras)
    ) {
      const allCamerasObject = CommonUtil.mapCollectionToObject({
        collection: availableCameras,
        key: 'id',
        clone: true,
      });
      setSelectedCameras(allCamerasObject);
    }
  }, [activity, availableCameras, selectedCameras]);

  const { startTimeString, endTimeString } = useMemo(() => {
    return TimeFormatter.getStartEndTimeString({
      startTime,
      endTime,
      locale,
      timezoneId: timezone,
    });
  }, [endTime, locale, startTime, timezone]);

  const alarmEnableNotice = useMemo(() => {
    if (typeof alarmEnable === 'undefined') {
      return '';
    }
    const hasPassed = moment().isAfter(startTime);
    if (hasPassed) {
      return LocaleUtil.getLocalizedStr({
        string: alarmEnable ? locale.alarms.wasArmedFromTo : locale.alarms.wasDisarmedFromTo,
        replacements: {
          start: startTimeString,
          end: endTimeString,
        },
      });
    }
    return LocaleUtil.getLocalizedStr({
      string: alarmEnable ? locale.alarms.willArmFromTo : locale.alarms.willDisarmFromTo,
      replacements: {
        start: startTimeString,
        end: endTimeString,
      },
    });
  }, [alarmEnable, endTimeString, locale, startTime, startTimeString]);

  const dateNotice = useMemo(() => {
    if (activity) {
      // The already scheduled event has to be cancelled. So thisExcStartTime definitely need to the initial start time.
      // For nextExcStartTime, if the start time has been changed, we need to display the new next start time as the time
      // it will take effect.
      const nextExcStartTime = newStartWithinOneHourFromNow
        ? displayDateTime(
            moment(newNextStartTime)
              .add(1, recurrence === REPEAT_MONTHLY ? 'month' : 'year')
              .valueOf(),
            timezone,
          )
        : displayDateTime(newNextStartTime, timezone);
      const thisExcStartTime = displayDateTime(initialStartTime, timezone);
      return (
        <>
          <p>
            {LocaleUtil.getLocalizedStr({
              string: locale.exceptions.takeEffectOnNext,
              replacements: {
                type: isScheduledAIChat
                  ? locale.scheduledAIChat.spotCheck
                  : locale.exceptions.scheduledException,
                date: nextExcStartTime,
              },
            })}
          </p>
          <p>
            {LocaleUtil.getLocalizedStr({
              string: locale.exceptions.exceptionWillBeCancelled,
              replacements: {
                type: isScheduledAIChat
                  ? locale.scheduledAIChat.spotCheck
                  : locale.exceptions.scheduledException,
                date: thisExcStartTime,
              },
            })}
          </p>
          <p>
            {LocaleUtil.getLocalizedStr({
              string: locale.exceptions.editAfterExcExecutes,
              replacements: {
                type: isScheduledAIChat
                  ? locale.scheduledAIChat.spotCheck
                  : locale.exceptions.scheduledException,
                date: thisExcStartTime,
              },
            })}
          </p>
        </>
      );
    }
    return null;
  }, [
    activity,
    newStartWithinOneHourFromNow,
    newNextStartTime,
    initialStartTime,
    recurrence,
    timezone,
    locale,
    isScheduledAIChat,
  ]);

  useTimeout(
    () => {
      const cameraSelectorHeight = cameraSelectorRef?.current?.clientHeight;
      const newStyle = {
        transform: `translate(0, ${
          isCameraSelectorOpen && cameraSelectorHeight ? `${cameraSelectorHeight}px` : 0
        })`,
        top: cameraSelectorHeight ? `${-cameraSelectorHeight}px` : 0,
      };

      setEventInfoStyles(newStyle);
    },
    10,
    [isCameraSelectorOpen, availableCameras],
  );

  const textfieldErrors = useMemo(() => {
    return {
      title: !title && isTouched.title ? locale.reports.fieldRequired : '',
      description: !description && isTouched.description ? locale.reports.fieldRequired : '',
      reason: !reason && isTouched.reason ? locale.reports.fieldRequired : '',
    };
  }, [isTouched, reason, title, description, locale.reports.fieldRequired]);

  const cameraSourceOptions = useMemo(() => {
    const areGroupsEmpty = groupsWithCameras.length === 0;
    const options: SelectOption[] = [
      {
        value: AI_INSPECTION_GROUP_OPTIONS.location,
        label: locale.dashboard.locations,
      },
    ];

    if (!areGroupsEmpty) {
      options.push({
        value: AI_INSPECTION_GROUP_OPTIONS.cameraGroup,
        label: locale.camera.cameraGroup,
      });
    }
    return options;
  }, [locale, groupsWithCameras]);

  const shouldShowAlarmExceptionsMoved = source && source === 'eventsPage';

  const showLoading = isLoading || (source === 'alarmsPage' && alarmCameras === undefined);

  const scheduledEventModalClassnames = classNames('scheduled-event-modal', {
    'scheduled-spot-check': isScheduledAIChat,
  });

  return (
    <Modal
      dataTestId={formTitle}
      isOpen={isOpen}
      title={formTitle}
      actions={modalActions}
      className={scheduledEventModalClassnames}
      type={isScheduledAIChat ? 'full-width' : 'extra-wide'}
      onClose={closeScheduledEventForm}
      footerChildren={
        <ActivityFormFooter
          isScheduledAIChat={isScheduledAIChat}
          spotCheckUsageStats={spotCheckUsageStats}
          activity={activity}
          hasImageCreditsError={hasImageCreditsError}
          leftoverImageCredits={leftoverImageCredits}
        />
      }
    >
      <Grid gridTemplateColumns="60% 40%" className="activity-form-grid-container">
        <Grid.Item
          className="activity-form-column"
          gridColumn={isScheduledAIChat ? '1/2' : '1/3'}
          justifySelf={isScheduledAIChat ? 'start' : 'stretch'}
        >
          {showLoading && (
            <Flex justifyContent="center" alignItems="center" className="loading-mask">
              <LoadingSpinner />
            </Flex>
          )}
          {!showLoading && (
            <>
              {!activity && !enforceReason && filterReasons && filterReasons.length > 1 && (
                <div className="event-detail select-type">
                  <Select
                    dataTestId="event-activity-type"
                    changeByProp="activity-type"
                    name="activity-type"
                    options={getActivityTypeOptions(locale, filterReasons)}
                    value={activityType}
                    onChange={onChange}
                    labelText={locale.general.selectActivityType}
                  />
                </div>
              )}
              {isScheduledAIChat && !viewOnly && (
                <div className="activity-camera-source">
                  <ActivityTitle title={locale.cameraSelector.cameraSource} />

                  <Select
                    name="camera-source"
                    onChange={onCameraSourceChange}
                    value={selectedDeviceType}
                    options={cameraSourceOptions}
                  />
                </div>
              )}
              {!enforceLocationId &&
                !viewOnly &&
                selectedDeviceType === AI_INSPECTION_GROUP_OPTIONS.location && (
                  <div className="event-detail select-location">
                    <div className="event-detail-title">{locale.general.selectLocation} *</div>
                    <LocationSelector
                      colorMode="light"
                      className="scheduled-activity-location-selector"
                      locations={locationSelectorLocations}
                      selectedLocationId={selectedLocationId}
                      onLocationSelected={handleLocationSelected}
                    />
                  </div>
                )}
              {!viewOnly && selectedDeviceType === AI_INSPECTION_GROUP_OPTIONS.cameraGroup && (
                <div className="event-detail select-camera-group">
                  <ActivityTitle title={locale.dashboard.cameraGroupPlaceholder} />
                  <Select
                    name="camera-group-select"
                    value={selectedCameraGroupId}
                    options={groupsWithCameras.map((group) => ({
                      value: group.id,
                      label: group.name,
                    }))}
                    onChange={onCameraGroupChange}
                  />
                </div>
              )}
              {shouldShowAlarmExceptionsMoved && (
                <p>
                  {locale.event.scheduledAlarmExceptionsMoved}{' '}
                  <Link style={{ color: 'var(--color-retro-blue-dark)' }} to={`/${ROUTE_ALARMS}`}>
                    {locale.general.alarmsPage}
                  </Link>
                  .
                </p>
              )}

              <div className="event-detail select-cameras">
                <div className="event-detail-title">
                  {canEditCameras ? locale.camera.cameras : `${locale.alarms.selectCameras} *`}
                </div>
                <div className="horizontal-camera-editor-wrapper">
                  <HorizontalCameraEditor
                    cameraSelectorRef={cameraSelectorRef}
                    canAdd={canEditCameras}
                    canEdit={canEditCameras}
                    selectedCameras={selectedCameras}
                    availableCameras={availableCameras}
                    originalCameras={initialSelectedCameras}
                    updateCamSelection={setSelectedCameras}
                    isCameraSelectorOpen={isCameraSelectorOpen}
                    updateCameraSelectorOpen={updateCameraSelectorOpen}
                    canEditOriginal={canEditOriginalCameras}
                    setEveryAlarmCamera={setEveryAlarmCamera}
                    everyAlarmCamera={everyAlarmCamera}
                    source={source}
                  />
                </div>
              </div>

              <div className="event-info-container">
                <div className="event-info" style={eventInfoStyles}>
                  {!viewOnly && selectedDeviceType === AI_INSPECTION_GROUP_OPTIONS.cameraGroup && (
                    <div className="event-detail select-timezone">
                      <p className="event-detail-title">{locale.general.selectTimezone}</p>
                      <CamerasTimezoneSelect
                        key={selectedCameraGroupId}
                        cameras={availableCameras}
                        selectedLocationId={selectedLocationId}
                        setSelectedLocationId={setSelectedLocationId}
                      />
                    </div>
                  )}
                  {isScheduledAIChat && <p className="event-title">{locale.general.startTime}</p>}
                  <ScheduledActivityTimePicker
                    disabled={viewOnly}
                    startTime={startTime}
                    endTime={endTime}
                    updateStartTime={updateStartTime}
                    updateEndTime={updateEndTime}
                    minDate={
                      viewOnly && isScheduledAIChat
                        ? undefined
                        : moment().add(5, 'minutes').toDate()
                    }
                    maxDate={maxDate}
                    timezoneId={timezone}
                    errorText={timeError}
                    showTimezoneNotice={
                      !isScheduledAIChat ||
                      selectedDeviceType !== AI_INSPECTION_GROUP_OPTIONS.cameraGroup
                    }
                    startDateOnly={isScheduledInspection || isScheduledAIChat}
                  />
                  {initialHasUnexcutedEvents && !viewOnly && startTime !== initialStartTime && (
                    <Flex className="date-notice" alignItems="center">
                      <MDIIcon icon={Info} size={18} />
                      <div className="notice-text">{dateNotice}</div>
                    </Flex>
                  )}
                  <div className="event-detail event-recurrence">
                    <Select
                      dataTestId="event-repeat"
                      changeByProp="recurrence"
                      name="recurrence"
                      options={recurrenceOptions}
                      value={recurrence}
                      onChange={onChange}
                      labelText={locale.general.repeat}
                      disabled={viewOnly}
                      containerClassName="event-recurrence-select"
                    />
                    {selectedDeviceType === 'LOCATION' &&
                      isScheduledAIChat &&
                      (recurrence === REPEAT_HOURLY ||
                        recurrence === REPEAT_EVERY_6H ||
                        recurrence === REPEAT_EVERY_4H) && (
                        <Select
                          dataTestId="weekly-schedule-option"
                          changeByProp="weeklyScheduleOption"
                          name="weeklyScheduleOption"
                          options={Object.entries(AI_CHAT_WEEKLY_SCHEDULE_OPTIONS).map(
                            ([key, value]) => ({
                              value: key,
                              label: locale.event[value.text],
                            }),
                          )}
                          value={weeklyScheduleOption}
                          onChange={onChange}
                          labelText={locale.event.weeklySchedule}
                          disabled={viewOnly}
                          containerClassName="event-schedule-select"
                        />
                      )}
                  </div>
                  {isScheduledAIChat &&
                    (recurrence === REPEAT_EVERY_6H ||
                      recurrence === REPEAT_DAILY ||
                      recurrence === REPEAT_EVERY_4H) && (
                      <div className="event-detail select-day-of-week">
                        <ActivityTitle
                          title={locale.general.activeDays}
                          icon={Info}
                          tooltipText={locale.scheduledAIChat.activeDaysTooltip}
                        />

                        <DaysOfWeekPicker
                          selectedDaysOfWeek={selectedDaysOfWeek}
                          setSelectedDaysOfWeek={setSelectedDaysOfWeek}
                          disabled={
                            !(
                              recurrence === REPEAT_EVERY_6H ||
                              recurrence === REPEAT_DAILY ||
                              recurrence === REPEAT_EVERY_4H
                            ) || viewOnly
                          }
                        />
                      </div>
                    )}
                  {isScheduledAIChat && recurrence !== DOES_NOT_REPEAT && (
                    <div className="event-detail select-expiry">
                      <ActivityTitle
                        title={locale.general.expiration}
                        icon={Info}
                        tooltipText={locale.scheduledAIChat.expirationTooltip}
                      />
                      <ActivityExpiryControl
                        selectedEndType={selectedEndType}
                        setSelectedEndType={setSelectedEndType}
                        timezoneId={timezone}
                        startTime={startTime}
                        updateEndTime={updateEndTime}
                        endTime={endTime}
                        setReoccurringCount={setReoccurringCount}
                        reoccurringCount={reoccurringCount}
                        reinitializingEndTime={initialEndTime}
                        disabled={viewOnly}
                        hasImageCreditsError={hasImageCreditsError}
                      />
                    </div>
                  )}
                  <div className="event-detail event-title">
                    <TextField
                      className={`event-title-input ${textfieldErrors['title'] ? 'error' : ''}`}
                      dataTestId="event-title"
                      changeByProp="title"
                      disabled={viewOnly}
                      labelText={`${
                        isScheduledInspection
                          ? locale.general.message
                          : locale.general.activityTitle
                      } *`}
                      name="title"
                      value={title}
                      onFocus={() => setIsTitleFocused(true)}
                      onBlur={() => {
                        setIsTitleFocused(false);
                        onBlur('title');
                      }}
                      onChange={onChange}
                      errorText={textfieldErrors['title']}
                      maxLength={titleMaxLength}
                    />
                    <div className={`char-available ${isTitleOverLimit ? 'char-over-limit' : ''}`}>
                      {isTitleFocused && titleCharactersAvailableString}
                    </div>
                  </div>
                  <div className="event-detail">
                    <ActivityTitle
                      title={`${
                        isScheduledAIChat ? locale.fields.prompt : locale.fields.description
                      } *`}
                      icon={isScheduledAIChat ? Info : undefined}
                      tooltipText={isScheduledAIChat ? locale.scheduledAIChat.promptTooltip : ''}
                    />
                    <TextArea
                      dataTestId="event-detail"
                      changeByProp="description"
                      disabled={viewOnly && !isScheduledAIChat}
                      name="description"
                      rows={1}
                      value={description}
                      onBlur={() => onBlur('description')}
                      onChange={onChange}
                      errorText={textfieldErrors['description']}
                      autoExpandTextarea={true}
                    />
                  </div>
                  {isScheduledAIChat && (
                    <div className="event-detail">
                      <ActivityTitle
                        title={locale.scheduledAIChat.responseFormat}
                        icon={Info}
                        tooltipText={locale.scheduledAIChat.responseFormatTooltip}
                      />
                      <TextArea
                        dataTestId="event-detail"
                        changeByProp="aiChatAnswerFormat"
                        name="aiChatAnswerFormat"
                        value={aiChatAnswerFormat}
                        disabled={viewOnly && !isScheduledAIChat}
                        placeholder={locale.scheduledAIChat.responseFormatPlaceholder}
                        rows={1}
                        onChange={onChange}
                        autoExpandTextarea={true}
                      />
                    </div>
                  )}
                  {!enforceReason && !isScheduledInspection && !isScheduledAIChat && (
                    <div className="event-detail">
                      <TextField
                        dataTestId="event-reason"
                        changeByProp="reason"
                        disabled={viewOnly}
                        labelText={`${locale.general.reason} *`}
                        name="reason"
                        value={reason}
                        onBlur={() => onBlur('reason')}
                        onChange={onChange}
                        errorText={textfieldErrors['reason']}
                      />
                    </div>
                  )}
                  <div className="event-detail">
                    {isAlarmScheduleException && (
                      <RadioGroup
                        checkedValue={alarmEnable?.toString()}
                        changeByProp="alarm-enable"
                        disabled={viewOnly}
                        name="alarm-enable"
                        title={
                          <>
                            <p>{locale.alarms.typeOfException} *</p>
                            <p className="hint-text">{locale.alarms.typeOfExceptionHint}</p>
                          </>
                        }
                        onChange={onChange}
                      >
                        <Radio
                          dataTestId="event-arm"
                          className="radio-option arm"
                          labelText={
                            <Flex>
                              <MDIIcon icon={AlarmOn} />
                              <div>{locale.alarms.arm}</div>
                            </Flex>
                          }
                          value="true"
                        />
                        <Radio
                          dataTestId="event-disarm"
                          className="radio-option disarm"
                          labelText={
                            <Flex>
                              <MDIIcon icon={AlarmOff} />
                              <div>{locale.alarms.disarm}</div>
                            </Flex>
                          }
                          value="false"
                        />
                      </RadioGroup>
                    )}
                    {typeof alarmEnable !== 'undefined' && isAlarmScheduleException && (
                      <Flex className="alarm-enable-notice" flexDirection="row" alignItems="center">
                        <MDIIcon icon={Info} />
                        <div className="notice-text">{alarmEnableNotice}</div>
                      </Flex>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </Grid.Item>
        {isScheduledAIChat && (
          <Grid.Item className="spot-check-preview-column" gridColumn="2/3">
            <Flex className="spot-check-preview-wrapper">
              <SpotCheckEventPreview
                key={spotCheckPreviewEventId ?? ''}
                eventPreviewId={spotCheckPreviewEventId}
                onCreateSpotCheckPreview={onCreateSpotCheckPreview}
                isPreviewDisabled={!canPreviewSpotCheck}
              />
            </Flex>
          </Grid.Item>
        )}
      </Grid>
    </Modal>
  );
};

export default ScheduledActivityForm;

/** Maps an array of cameras to event cameras object */
function getEventCamerasObject(cameras: Array<CallhomeCamera | CamerasDictionaryCamera>): {
  [key: string]: any;
} {
  const eventCameras = cameras?.map((camera) => callhomeCameraToEventCamera(camera));

  return CommonUtil.mapCollectionToObject({
    collection: eventCameras,
    key: 'id',
    clone: true,
  });
}
